import { data as routerData } from 'react-router';
import type { MetaDescriptor } from 'react-router';
import type { LoaderFunctionArgs } from '~/types/route-args';
import OurProductsPage from '@modules/professional/page/OurProductsPage';
import { loader as ourProductsLoader } from '@modules/professional/page/loaders/our-products-page-loader.server';
import { isProfessionalRevampActive } from '@modules/professional/util/is-professional-revamp-active.server';
import { getSession } from '~/modules/root/util/session.server';
import { ProfessionalProductsPageRevamp } from '~/ui-rocco/pages/professional-detail/ProfessionalProductsPageRevamp';
import { mergeMeta } from '~/modules/seo';
import { shouldRevalidateLeaf } from '~/modules/root/util/should-revalidate-leaf';
import type { APHandle } from '~/modules/root';
import { useShowProfessionalRevamp } from '@modules/professional/hook/use-professional-layout-data';

/**
 * When the professional revamp is active the Products tab becomes a full
 * editorial page (light-theme sibling of the Projects tab) that reuses the
 * overview sections, so it serves overview-shaped data merged with the products
 * grid data. Otherwise it serves the legacy paginated products grid.
 */
export const loader = async (args: LoaderFunctionArgs) => {
    if (!isProfessionalRevampActive(args.context, args.request)) {
        return ourProductsLoader(args);
    }

    // The revamp's shared overview payload comes from the persistent layout
    // loader; this route only fetches the products tab's own data.
    const productsData = await ourProductsLoader(args);
    const session = await getSession(args.request);

    return routerData(
        {
            productTiles: productsData.productTiles,
            productTilesRocco: productsData.productTilesRocco,
            pagination: productsData.pagination,
            filterStates: productsData.filterStates,
            filterStatesRocco: productsData.filterStatesRocco,
            searchResult: productsData.searchResult,
            brandScope: productsData.brandScope,
        },
        { headers: await session.getHeaders() },
    );
};

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    () => {
        const preloadImageLinks: MetaDescriptor[] = [];
        return [...preloadImageLinks];
    },
);

export const handle: APHandle = {
    category: 'product',
    hideContactArchiPro: true,
    professionalRevampLayout: true,
};

const ProfessionalProductsRoute = () => {
    const showRevamp = useShowProfessionalRevamp();

    return showRevamp ? (
        <ProfessionalProductsPageRevamp />
    ) : (
        <OurProductsPage />
    );
};

export const shouldRevalidate = shouldRevalidateLeaf;
export default ProfessionalProductsRoute;
